// components/UpdateStationModal.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import StationService from "../services/station_service";
import { ApiBaseUrl } from "../../http-common";
import InputDropdown from "./input_dropdown";


const UpdateInfoStationModal = ({ station, onClose, onUpdated }) => {
  const [formData, setFormData] = useState({
    station_id: "",
    station_code: "",
    name: "",
    name_hindi: "",
    chainage: "",
    gradient_status: "Applicable",
    tag_range: "",
    tin_range: "",
    sip_reference: "",
    zone: "",
    division: "",
    section: "",
    ref_toc: "",
    ref_rct: "",
    ref_layout: "",
    rfid_toc: "",
    ref_td: "",
    div_section: "",
    atp_records: [{ atp_territory: "", entry_exit: "", signal_name: "" }],
    boundaries: [{ line_name: "", signal_name: "", signal_type: "" }],
    gradients: [],
    psrs: [{ yes_no: "", speed: "" }],
  });

  const DIV_SECTION_OPTIONS = [
    "KGP",
    "CKP",
    "GRC",
    "PRYG",
    "AJJ-RU",
    "AJJ-JTJ",
    "AKM-GDR",
    "MAS-AJJ",
    "MAS-GDR",
  ];

  const [showDivSectionDropdown, setShowDivSectionDropdown] = useState(false);
  const [showGradientUpload, setShowGradientUpload] = useState(false);
  const [uploadValues, setUploadValues] = useState({
    file: null,
    fromAbsValue: "",
    toAbsValue: "",
  });

  // Pre-populate form when station prop changes
  useEffect(() => {
    if (station) {
      setFormData({
        station_id: station.station_id || "",
        station_code: station.station_code || "",
        name: station.name || "",
        name_hindi: station.name_hindi || "",
        chainage: station.chainage || "",
        gradient_status: station.gradient_status || "Applicable",
        tag_range: station.tag_range || "",
        tin_range: station.tin_range || "",
        sip_reference: station.sip_reference || "",
        zone: station.zone || "",
        division: station.division || "",
        section: station.section || "",
        ref_toc: station.ref_toc || "",
        ref_rct: station.ref_rct || "",
        ref_layout: station.ref_layout || "",
        rfid_toc: station.rfid_toc || "",
        ref_td: station.ref_td || "",
        div_section: station.div_section || "KGP",
        atp_records:
          station.atp_records && station.atp_records.length > 0
            ? station.atp_records.map(a => ({
              atp_territory: a.atp_territory || "",
              entry_exit: a.entry_exit || "",
              signal_name: a.signal_name || "",
            }))
            : [{ atp_territory: "", entry_exit: "", signal_name: "" }],
        boundaries:
          station.boundaries && station.boundaries.length > 0
            ? station.boundaries.map(b => ({
              line_name: b.line_name || "",
              signal_name: b.signal_name || "",
              signal_type: b.signal_type || "",
            }))
            : [{ line_name: "", signal_name: "", signal_type: "" }],
        gradients:
          station.gradients && station.gradients.length > 0
            ? station.gradients.map(g => ({
              lines: g.lines || "",
              from_abs: g.from_abs || "",
              to_abs: g.to_abs || "",
              length: g.length || "",
              direction: g.direction || "",
              grade: g.grade || "",
            }))
            : [],
        psrs:
          station.psrs && station.psrs.length > 0
            ? station.psrs.map(p => ({
              yes_no: p.yes_no || "",
              speed: p.speed || "",
            }))
            : [{ yes_no: "", speed: "" }],
      });
    }
  }, [station]);

  const handleChange = (field, value) => {
    if (field === "gradient_status" && value === "Not Applicable") {
      setFormData({ ...formData, gradient_status: value, gradients: [] });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleArrayChange = (key, index, field, value) => {
    const updated = [...formData[key]];
    updated[index][field] = value;
    setFormData({ ...formData, [key]: updated });
  };

  const addRow = (key) => {
    const empty =
      key === "boundaries"
        ? { line_name: "", signal_name: "", signal_type: "" }
        : key === "gradients"
          ? { lines: "", from_abs: "", to_abs: "", length: "", direction: "", grade: "" }
          : { yes_no: "", speed: "" };
    setFormData({ ...formData, [key]: [...formData[key], empty] });
  };

  const removeRow = (key, index) => {
    const updated = formData[key].filter((_, i) => i !== index);
    const empty =
      key === "boundaries"
        ? { line_name: "", signal_name: "", signal_type: "" }
        : key === "gradients"
          ? { lines: "", from_abs: "", to_abs: "", length: "", direction: "", grade: "" }
          : { yes_no: "", speed: "" };
    setFormData({
      ...formData,
      [key]: updated.length ? updated : [empty],
    });
  };

  const handleATPChange = (index, field, value) => {
    const updated = [...formData.atp_records];
    updated[index][field] = value;
    setFormData({ ...formData, atp_records: updated });
  };

  const addATP = () => {
    setFormData({
      ...formData,
      atp_records: [
        ...formData.atp_records,
        { atp_territory: "", entry_exit: "", signal_name: "" },
      ],
    });
  };

  const removeATP = (index) => {
    const updated = formData.atp_records.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      atp_records:
        updated.length
          ? updated
          : [{ atp_territory: "", entry_exit: "", signal_name: "" }],
    });
  };

  // Gradient file upload — replaces entire gradient list
  const getGradientData = async () => {
    if (!uploadValues.file) {
      toast.error("Please choose a file");
      return;
    }

    const form = new FormData();
    form.append("file", uploadValues.file);
    form.append("fromAbsValue", uploadValues.fromAbsValue);
    form.append("toAbsValue", uploadValues.toAbsValue);

    try {
      const res = await fetch(`${ApiBaseUrl}/gradient/getAll`, {
        method: "POST",
        body: form,
      });
      const data = await res.json();

      const newGradientRows = data.map((rec) => ({
        lines: rec.line ?? "",
        from_abs: rec.fromAbs ?? "",
        to_abs: rec.toAbs ?? "",
        length: rec.length ?? "",
        direction: rec.direction ?? "",
        grade: rec.grade ?? "",
      }));

      // Completely replace gradients
      setFormData((prev) => ({
        ...prev,
        gradients: newGradientRows,
      }));

      toast.success("Gradient data replaced successfully");
      setShowGradientUpload(false);
      setUploadValues({ file: null, fromAbsValue: "", toAbsValue: "" });
    } catch (err) {
      toast.error("Upload error: " + (err.message || "Unknown error"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        station_id: parseInt(formData.station_id),
        name: formData.name,
        name_hindi: formData.name_hindi,
        station_code: formData.station_code,
        chainage: formData.chainage ? parseFloat(formData.chainage) : null,
        gradient_status: formData.gradient_status,
        boundaries: formData.boundaries,
        tag_range: formData.tag_range,
        tin_range: formData.tin_range,
        sip_reference: formData.sip_reference,
        zone: formData.zone,
        division: formData.division,
        section: formData.section,
        atp_records: formData.atp_records,
        ref_toc: formData.ref_toc,
        ref_rct: formData.ref_rct,
        ref_layout: formData.ref_layout,
        rfid_toc: formData.rfid_toc,
        ref_td: formData.ref_td,
        div_section: formData.div_section,
        gradients: formData.gradients.map((g) => ({
          lines: g.lines,
          from_abs: g.from_abs ? parseFloat(g.from_abs) : null,
          to_abs: g.to_abs ? parseFloat(g.to_abs) : null,
          length: g.length ? parseFloat(g.length) : null,
          direction: g.direction,
          grade: g.grade,
        })),
        psrs: formData.psrs.map((p) => ({
          yes_no: p.yes_no,
          speed: p.speed ? parseInt(p.speed) : null,
        })),
      };

      await StationService.updateInfoStation(station.station_id, data);
      toast.success("Station updated successfully!");
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update station");
    }
  };

  const showGradients = formData.gradient_status === "Applicable";

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content p-3">
          <div className="modal-header">
            <h5>Update Station</h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {/* Same layout as Create modal */}
              <div className="row mb-3">
                <div className="col">
                  <label>Station ID</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.station_id}
                    onChange={(e) => handleChange("station_id", e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-3">
                  <label>Station Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    required
                  />
                </div>
                <div className="col">
                  <label>Station CODE</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.station_code}
                    onChange={(e) => handleChange("station_code", e.target.value)}
                    required
                  />
                </div>
                <div className="col">
                  <InputDropdown
                    label="Section"
                    value={formData.section}
                    options={[
                      "Station",
                      "Block",
                      "Hut"]}
                    onChange={(value) => handleChange("section", value)}
                  />
                </div>
                <div className="col">
                  <InputDropdown
                    label="Division"
                    value={formData.division}
                    options={[
                      "KHARAGPUR",
                      "CHAKRADHARPUR",
                      "CHENNAI",
                      "PRAYAGRAJ",
                      "TIKIAPARA",
                    ]}
                    onChange={(value) => handleChange("division", value)}
                  />
                </div>
                <div className="col">
                  <InputDropdown
                    label="Zone"
                    value={formData.zone}
                    options={[
                      { label: "SER", value: "SOUTH EASTERN RAILWAY" },
                      { label: "SR", value: "SOUTH RAILWAY" },
                      { label: "SCR", value: "SOUTH CENTRAL RAILWAY" },
                      { label: "NCR", value: "NORTH CENTRAL RAILWAY" },
                      { label: "WR", value: "WESTERN RAILWAY" },
                      { label: "ER", value: "EASTERN RAILWAY" },
                    ]}
                    onChange={(value) => handleChange("zone", value)}
                  />
                </div>
                {/* <div className="col">
                  <InputDropdown
                    label="Zone"
                    value={formData.zone}
                    options={[
                      "SOUTH EASTERN RAILWAY",
                      "SOUTH RAILWAY",
                      "SOUTH CENTRAL RAILWAY",
                      "NORTH CENTRAL RAILWAY",
                      "WESTERN RAILWAY",
                      "EASTERN RAILWAY",
                    ]}
                    onChange={(value) => handleChange("zone", value)}
                  />
                </div> */}
              </div>

              <div className="row mb-3">
                <div className="col">
                  <label>TAG Range</label>
                  <input
                    type="text"
                    placeholder="1-100,101-200.."
                    className="form-control"
                    value={formData.tag_range}
                    onChange={(e) => handleChange("tag_range", e.target.value)}
                  />
                </div>
                <div className="col">
                  <label>TIN Range</label>
                  <input
                    type="text"
                    placeholder="1-100|101-200.."
                    className="form-control"
                    value={formData.tin_range}
                    onChange={(e) => handleChange("tin_range", e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label>Chainage (km)</label>
                  <input
                    type="number"
                    step="1"
                    className="form-control"
                    value={formData.chainage}
                    onChange={(e) => handleChange("chainage", e.target.value)}
                  />
                </div>
                <div className="col-md-1">
                  <label>PSR</label>
                  {formData.psrs.map((p, i) => (
                    <input
                      key={i}
                      type="number"
                      placeholder="Speed"
                      className="form-control mb-1"
                      value={p.speed}
                      onChange={(e) =>
                        handleArrayChange("psrs", i, "speed", e.target.value)
                      }
                    />
                  ))}
                </div>
                <div className="col-md-2">
                  <label>SIP Reference</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.sip_reference}
                    onChange={(e) => handleChange("sip_reference", e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  {/* <label>Div-Section</label>
                  <select className="form-select" value={formData.div_section} onChange={e => handleChange("div_section", e.target.value)}>
                    <option value="KGP">KGP</option>
                    <option value="CKP">CKP</option>
                    <option value="GRC">GRC</option>
                    <option value="PRYG">PRYG</option>
                    <option value="AJJ-RU">AJJ-RU</option>
                  </select> */}
                  <InputDropdown
                    label="Div-Section"
                    value={formData.div_section}
                    options={[
                      "KGP",
                      "CKP",
                      "GRC",
                      "PRYG",
                      "AJJ-RU",
                      "AJJ-JTJ",
                      "AKM-GDR",
                      "MAS-AJJ",
                      "MAS-GDR",
                    ]}
                    onChange={(value) => handleChange("div_section", value)}
                  />
                </div>
              </div>


              <div className="d-flex justify-content-between mb-3">
                <div className="">
                  <label>Station Hindi name</label>
                  <input type="text" placeholder="" className="form-control" value={formData.name_hindi} onChange={e => handleChange("name_hindi", e.target.value)} />
                </div>
                <div className="">
                  <label>Ref ICT/TOC</label>
                  <input type="text" placeholder="" className="form-control" value={formData.ref_toc} onChange={e => handleChange("ref_toc", e.target.value)} />
                </div>
                <div className="">
                  <label>RFID TOC</label>
                  <input type="text" placeholder="" className="form-control" value={formData.rfid_toc} onChange={e => handleChange("rfid_toc", e.target.value)} />
                </div>
                <div className="">
                  <label>Ref Layout</label>
                  <input type="text" placeholder="" className="form-control" value={formData.ref_layout} onChange={e => handleChange("ref_layout", e.target.value)} />
                </div>
                <div className="">
                  <label>Ref TD</label>
                  <input type="text" placeholder="" className="form-control" value={formData.ref_td} onChange={e => handleChange("ref_td", e.target.value)} />
                </div>
              </div>

              {/* ATP & Boundaries */}
              <div className="row">
                <div className="col">
                  <h6>ATP Territory</h6>
                  {formData.atp_records.map((a, i) => (
                    <div key={i} className="row mb-2 align-items-end">
                      <div className="col">
                        <select
                          className="form-select"
                          value={a.entry_exit}
                          onChange={(e) =>
                            handleATPChange(i, "entry_exit", e.target.value)
                          }
                        >
                          <option value="">Entry/Exit</option>
                          <option>Entry</option>
                          <option>Exit</option>
                        </select>
                      </div>
                      <div className="col">
                        <input
                          placeholder="Signal Name"
                          className="form-control"
                          value={a.signal_name}
                          onChange={(e) =>
                            handleATPChange(i, "signal_name", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-auto">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeATP(i)}
                        >
                          X
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm mb-3"
                    onClick={addATP}
                  >
                    + Add ATP
                  </button>
                </div>

                <div className="col">
                  <h6>Station Boundaries</h6>
                  {formData.boundaries.map((b, i) => (
                    <div key={i} className="row mb-2 align-items-end">
                      <div className="col">
                        <input
                          placeholder="Line"
                          className="form-control"
                          value={b.line_name}
                          onChange={(e) =>
                            handleArrayChange(
                              "boundaries",
                              i,
                              "line_name",
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="col">
                        <input
                          placeholder="Signal"
                          className="form-control"
                          value={b.signal_name}
                          onChange={(e) =>
                            handleArrayChange(
                              "boundaries",
                              i,
                              "signal_name",
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="col">
                        <select
                          className="form-select"
                          value={b.signal_type}
                          onChange={(e) =>
                            handleArrayChange(
                              "boundaries",
                              i,
                              "signal_type",
                              e.target.value
                            )
                          }
                        >
                          <option>Type</option>
                          <option>Entry</option>
                          <option>Exit</option>
                        </select>
                      </div>
                      <div className="col-auto">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeRow("boundaries", i)}
                        >
                          X
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm mb-3"
                    onClick={() => addRow("boundaries")}
                  >
                    + Add Boundary
                  </button>
                </div>
              </div>

              {/* Gradients */}
              {showGradients && (
                <>
                  <h6>Gradient Information</h6>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm mb-2"
                    onClick={() => setShowGradientUpload(true)}
                  >
                    Upload New Gradient Data (Replaces All)
                  </button>

                  {formData.gradients.map((g, i) => (
                    <div key={i} className="row mb-2 align-items-end">
                      <div className="col">
                        <input
                          placeholder="Lines"
                          className="form-control"
                          value={g.lines}
                          onChange={(e) =>
                            handleArrayChange("gradients", i, "lines", e.target.value)
                          }
                        />
                      </div>
                      <div className="col">
                        <input
                          type="number"
                          step="0.1"
                          placeholder="From"
                          className="form-control"
                          value={g.from_abs}
                          onChange={(e) =>
                            handleArrayChange("gradients", i, "from_abs", e.target.value)
                          }
                        />
                      </div>
                      <div className="col">
                        <input
                          type="number"
                          step="0.1"
                          placeholder="To"
                          className="form-control"
                          value={g.to_abs}
                          onChange={(e) =>
                            handleArrayChange("gradients", i, "to_abs", e.target.value)
                          }
                        />
                      </div>
                      <div className="col">
                        <input
                          type="number"
                          step="0.1"
                          placeholder="Length"
                          className="form-control"
                          value={g.length}
                          onChange={(e) =>
                            handleArrayChange("gradients", i, "length", e.target.value)
                          }
                        />
                      </div>
                      <div className="col">
                        <input
                          placeholder="Dir"
                          className="form-control"
                          value={g.direction}
                          onChange={(e) =>
                            handleArrayChange("gradients", i, "direction", e.target.value)
                          }
                        />
                      </div>
                      <div className="col">
                        <input
                          placeholder="Grade"
                          className="form-control"
                          value={g.grade}
                          onChange={(e) =>
                            handleArrayChange("gradients", i, "grade", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-auto">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeRow("gradients", i)}
                        >
                          X
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm mb-3"
                    onClick={() => addRow("gradients")}
                  >
                    + Add Gradient Row
                  </button>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button type="submit" className="btn btn-success">
                Update
              </button>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>

          {/* Gradient Upload Overlay */}
          {showGradientUpload && (
            <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
              <div className="modal-dialog">
                <div className="modal-content p-3">
                  <h5>Upload New Gradient Data</h5>
                  <p className="text-danger">
                    This will <strong>replace all existing gradient rows</strong>.
                  </p>

                  <div className="mb-2 mt-4">
                    <label>Select File</label>
                    <input
                      type="file"
                      className="form-control"
                      onChange={(e) =>
                        setUploadValues({ ...uploadValues, file: e.target.files[0] })
                      }
                    />
                  </div>

                  <div className="row">
                    <div className="col mb-2">
                      <label>From ABS Value</label>
                      <input
                        type="number"
                        className="form-control"
                        value={uploadValues.fromAbsValue}
                        onChange={(e) =>
                          setUploadValues({ ...uploadValues, fromAbsValue: e.target.value })
                        }
                      />
                    </div>
                    <div className="col mb-2">
                      <label>To ABS Value</label>
                      <input
                        type="number"
                        className="form-control"
                        value={uploadValues.toAbsValue}
                        onChange={(e) =>
                          setUploadValues({ ...uploadValues, toAbsValue: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="text-end mt-5">
                    <button className="btn btn-success me-2" onClick={getGradientData}>
                      Submit & Replace
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowGradientUpload(false);
                        setUploadValues({ file: null, fromAbsValue: "", toAbsValue: "" });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateInfoStationModal;